import { recordPath, recordRoute } from '@/shared/registry'

/** Where Find Users lives. */
export const usersPath = '/site-management/users'

/** Where Create User, User Details and Edit User live. */
export const createUserPath = `${usersPath}/new`
export const userRoute = recordRoute('site-management', 'users')
export const editUserRoute = `${userRoute}/edit`

/** A user's details page. */
export function userPath(id: string): string {
  return recordPath('site-management', 'users', id)
}

/** A user's Edit User page. */
export function editUserPath(id: string): string {
  return `${userPath(id)}/edit`
}

/** The API's user search, and its export at `${usersApi}/export`; a POST creates a user. */
export const usersApi = '/site-management/v1/users'

/** The API's user: GET reads it, PUT changes it. */
export function userApi(id: string): string {
  return `${usersApi}/${encodeURIComponent(id)}`
}

/** The API's deactivation or reactivation of a user. */
export function userStatusApi(id: string, action: 'deactivate' | 'reactivate'): string {
  return `${userApi(id)}/${action}`
}

/** The API's merge of a duplicate person into another. */
export const mergeUsersApi = `${usersApi}/merge`

/** The API's preview of that merge. */
export function mergePreviewApi(keptId: string, mergedId: string): string {
  return `${usersApi}/merge-preview?${new URLSearchParams({ keptId, mergedId }).toString()}`
}

/** The event a page that changed a user raises, so User Details reads it again. */
export const userChangedEvent = 'site-management:user-changed'

/** What a {@link userChangedEvent} carries: the user changed, and what to tell the caller about it. */
export interface UserChange {
  readonly recordId: string
  readonly message: string
}

/** The API's erasure of a person. */
export function eraseUserApi(id: string): string {
  return `${userApi(id)}/erase`
}

/** What a caller types to erase a person, as the API checks it. */
export const eraseConfirmation = 'ERASE'

/** The API that emails a person who cannot sign in a link to download their data. */
export function userDataExportApi(id: string): string {
  return `${userApi(id)}/data-export`
}

/** The longest note about a verified request the API keeps. */
export const requestNoteLength = 1000

/** The API's move of a user to an organization on another site. */
export function userOrganizationApi(id: string): string {
  return `${userApi(id)}/organization`
}

/** The API's retry or cancel of a merge of a user's person that stays pending. */
export function userMergeRecoveryApi(id: string, action: 'retry' | 'cancel'): string {
  return `${userApi(id)}/merge/${action}`
}

/** The API's retry or cancel of a user's deactivation or move that stays pending. */
export function userPendingChangeApi(id: string, action: 'retry' | 'cancel'): string {
  return `${userApi(id)}/pending-change/${action}`
}

/** The API's organizations on another site that users may be moved to. */
export function userMoveOrganizationsApi(siteId: string): string {
  return `${usersApi}/move-organizations/${encodeURIComponent(siteId)}`
}

/** The API that closes the caller's own account on this site. */
export const closeAccountApi = '/site-management/v1/me/close'

/** What a person types to close their account, as the API checks it. */
export const closeConfirmation = 'CLOSE'

/** The API's display names of User Profiles, the `User` name lookup. */
export const userNamesApi = '/site-management/v1/user-profile-names'

/** What the user screens need: read to find and see people, write to add and change them, and an aspect per guarded part. */
export const readUsersPermission = 'site-management.user-profiles.read'
export const writeUsersPermission = 'site-management.user-profiles.write'
/** Create User needs write at a scope that reaches organizations, as the API checks; own scope reaches none. */
export const createUserScopes = ['allOrganizations', 'managedOrganizations'] as const
export const readSignInHistoryPermission = 'site-management.user-profiles.sign-in-history.read'
export const writeUserOrganizationPermission = 'site-management.user-profiles.organization.write'
export const writeUserManagerPermission = 'site-management.user-profiles.manager.write'
export const writeUserCompanyPermission = 'site-management.user-profiles.company.write'
export const writeUserExternalIdPermission = 'site-management.user-profiles.external-id.write'
export const writeUserOptInsPermission = 'site-management.user-profiles.opt-ins.write'
export const writeUserPersonalDetailsPermission = 'site-management.user-profiles.personal-details.write'
export const writeUserStatusPermission = 'site-management.user-profiles.status.write'
export const writeUserDoNotReactivatePermission = 'site-management.user-profiles.do-not-reactivate.write'
export const eraseUserPermission = 'site-management.user-profiles.erase.write'
export const dataExportUserPermission = 'site-management.user-profiles.data-export.write'
export const mergeUsersPermission = 'site-management.user-profiles.merge.write'
